'use client';

// ─── Types (shared with SkuInventoryForm & VariantsTable) ──────────────────────

export interface CategoryAttributeDetail {
  id: string | null;
  nameEn: string;
  nameAr: string;
  name: string;
}

export interface CategoryAttribute {
  id: string;
  nameEn: string;
  nameAr: string;
  name: string;
  isColor: boolean;
  categoryAttributeDetails: CategoryAttributeDetail[];
}

export interface PermutationSlot {
  attrId: string;
  detail: CategoryAttributeDetail;
}

export interface VariantRow {
  _permutationKey: string;
  _displayDetails: PermutationSlot[];
  sku: string | null;
  stock: number;
  price?: number;
  priceConverted?: number;
}

interface ColorEntry {
  color: { id: string | null; name: string } | undefined;
  image?: { uid: string; url?: string; uploading?: boolean }[];
}

// ─── Cartesian Product ─────────────────────────────────────────────────────────

function cartesian(arrays: PermutationSlot[][]): PermutationSlot[][] {
  if (arrays.length === 0) return [[]];
  return arrays.reduce<PermutationSlot[][]>(
    (acc, arr) => acc.flatMap((d) => arr.map((e) => [...d, e])),
    [[]]
  );
}

// ─── computePermutations ──────────────────────────────────────────────────────

/**
 * Pure function. Computes the full cartesian product of selected attribute
 * values and colors. Does NOT read or write form state.
 *
 * @param attributes      Full list of category attributes (from API)
 * @param watchedAttrs    Record<attrId, selectedDetailIds[]> (from form)
 * @param watchedColors   Array of color entries (from form)
 */
export function computePermutations(
  attributes: CategoryAttribute[],
  watchedAttrs: Record<string, string[]>,
  watchedColors: ColorEntry[]
): PermutationSlot[][] {
  const nonColorAttributes = attributes.filter((a) => !a.isColor);
  const dimensions: PermutationSlot[][] = [];

  nonColorAttributes.forEach((attr) => {
    const selectedIds: string[] = watchedAttrs[attr.id] || [];
    if (selectedIds.length > 0) {
      const selectedDetails = attr.categoryAttributeDetails.filter((d) =>
        selectedIds.includes(d.id as string)
      );
      if (selectedDetails.length > 0) {
        dimensions.push(selectedDetails.map((detail) => ({ attrId: attr.id, detail })));
      }
    }
  });

  const colorDetails = watchedColors
    .filter((c) => c?.color?.name)
    .map((c) => ({
      attrId: 'color',
      detail: {
        id: c.color!.id,
        name: c.color!.name,
        nameEn: c.color!.name,
        nameAr: c.color!.name,
      },
    }));

  if (colorDetails.length > 0) {
    dimensions.push(colorDetails);
  }

  if (dimensions.length === 0) return [];

  return cartesian(dimensions);
}

// ─── syncVariantsToPermutations ───────────────────────────────────────────────

/**
 * Diffs the new permutation set against the current variant rows and
 * calls append/remove to bring the form state in sync.
 *
 * Rules:
 *  - Existing rows that still match a permutation key are NEVER touched.
 *  - New permutation keys get a fresh row appended: { sku: null, stock: 0, price: 0 }
 *  - Rows whose key is no longer in the new set are removed (by index, highest first).
 */
export function syncVariantsToPermutations(
  permutations: PermutationSlot[][],
  currentVariants: VariantRow[],
  appendVariant: (row: Omit<VariantRow, 'id'>) => void,
  removeVariant: (index: number) => void
): void {
  const newPermKeys = new Set(
    permutations.map((perm) => perm.map((p) => p.detail.name).sort().join('-'))
  );

  // Collect indices to remove (rows whose key is no longer valid)
  const indicesToRemove: number[] = [];
  currentVariants.forEach((v, idx) => {
    if (v._permutationKey && !newPermKeys.has(v._permutationKey)) {
      indicesToRemove.push(idx);
    }
  });

  // Remove from highest index to lowest so indices don't shift
  [...indicesToRemove].sort((a, b) => b - a).forEach((idx) => removeVariant(idx));

  // Build current key set after removals
  const remainingKeys = new Set(
    currentVariants
      .filter((_, idx) => !indicesToRemove.includes(idx))
      .map((v) => v._permutationKey)
  );

  // Append new rows for permutations that don't exist yet
  permutations.forEach((perm) => {
    const permKey = perm.map((p) => p.detail.name).sort().join('-');
    if (!remainingKeys.has(permKey)) {
      appendVariant({
        _permutationKey: permKey,
        _displayDetails: perm,
        sku: null,
        stock: 0,
        price: 0,
        priceConverted: 0,
      });
    }
  });
}
